import { useEffect } from 'react'
import { NextSeo, ArticleJsonLd } from 'next-seo'
import Prism from 'prismjs'
import type { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from 'next'
import { getPost, getPosts, PostOrPage } from '@src/lib/ghost'
import { formatDate } from '@src/lib/utils'
import SiteLayout from '@src/ui/components/site-layout'
import { postJsonLd, postOpenGraphData } from '@src/lib/seo'

require('prismjs/components/prism-bash.min')
require('prismjs/components/prism-swift.min')
require('prismjs/components/prism-toml.min')
require('prismjs/components/prism-yaml.min')

type PostProps = {
  post: PostOrPage
}
const Post = (props: PostProps) => {
  useEffect(() => {
    Prism.highlightAll()
  }, [props.post])
  return (
    <SiteLayout>
      <NextSeo
        title={props.post.title}
        description={props.post.excerpt}
        canonical={`https://swift.cloud/blog/${props.post.slug}`}
        openGraph={postOpenGraphData(props.post)}
      />
      <div className='sm:px-6 lg:pt-24 lg:pb-28 lg:px-8 relative px-4 pt-8 pb-20 bg-white'>
        <div className='max-w-7xl relative mx-auto'>
          <div className='text-center'>
            <p className='text-slate-600 mb-4 text-base'>
              {formatDate(props.post.published_at as string)}
              <span className='inline-block mx-3' aria-hidden='true'>
                &middot;
              </span>
              {props.post.reading_time} minute read
            </p>
            <h1 className='sm:text-5xl text-slate-900 text-3xl font-bold tracking-tight'>
              {props.post.title}
            </h1>
            <div className='flex flex-col items-center mt-8 space-y-2'>
              {props.post.primary_author?.profile_image ? (
                <img
                  className='w-12 h-12 rounded-full'
                  src={props.post.primary_author?.profile_image}
                  alt={props.post.primary_author?.name}
                />
              ) : null}
              <p className='text-slate-900 text-base font-medium leading-tight'>
                {props.post.primary_author?.name}
                <span className='text-slate-600 block text-sm'>
                  {props.post.primary_author?.bio}
                </span>
              </p>
            </div>
          </div>
          <div className='max-w-3xl gap-5 mx-auto mt-12'>
            <div className='post' dangerouslySetInnerHTML={{ __html: props.post.html as string }} />
          </div>
        </div>
      </div>
      <ArticleJsonLd {...postJsonLd(props.post)} />
    </SiteLayout>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = await getPosts()
  const paths = posts.map((post) => ({
    params: { slug: post.slug }
  }))
  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  const slug = context?.params?.slug as string
  const post = await getPost(slug)
  if (!post) {
    return {
      notFound: true
    }
  }
  return {
    props: { post },
    revalidate: 60
  }
}

export default Post
